/**
 * usePolling — polling com Page Visibility API e backoff exponencial opcional
 *
 * @param {Function} fn        — função async a executar periodicamente
 * @param {Object}   options
 *   @param {number}   baseInterval  — intervalo base em ms (padrão: 5000)
 *   @param {boolean}  enabled       — se false, pausa o polling (padrão: true)
 *   @param {boolean}  backoff       — ativa backoff exponencial quando fn retorna false (padrão: false)
 *   @param {number}   maxInterval   — intervalo máximo com backoff em ms (padrão: 30000)
 *   @param {boolean}  pauseHidden   — pausa quando aba está inativa (padrão: true)
 *
 * Uso com backoff:
 *   fn deve retornar true quando há novos dados (reseta o backoff)
 *   fn deve retornar false quando não há novos dados (incrementa o backoff)
 */
import { useEffect, useRef, useCallback } from 'react';

export function usePolling(fn, {
  baseInterval = 5000,
  enabled      = true,
  backoff      = false,
  maxInterval  = 30000,
  pauseHidden  = true,
} = {}) {
  const timerRef    = useRef(null);
  const intervalRef = useRef(baseInterval);
  const fnRef       = useRef(fn);

  // Mantém referência atualizada sem re-criar o efeito
  useEffect(() => { fnRef.current = fn; }, [fn]);

  const schedule = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      // Pausa se aba está inativa
      if (pauseHidden && document.hidden) {
        schedule();
        return;
      }

      const hasNewData = await fnRef.current().catch(() => false);

      if (backoff) {
        if (hasNewData) {
          // Reseta para o intervalo base
          intervalRef.current = baseInterval;
        } else {
          // Dobra o intervalo até o máximo
          intervalRef.current = Math.min(intervalRef.current * 2, maxInterval);
        }
      }

      schedule();
    }, intervalRef.current);
  }, [backoff, baseInterval, maxInterval, pauseHidden]); // eslint-disable-line

  useEffect(() => {
    if (!enabled) {
      console.log('[Polling Debug] DISABLED — clearing timer');
      clearTimeout(timerRef.current);
      return;
    }

    console.log(`[Polling Debug] ENABLED — starting with interval=${baseInterval}ms`);
    // Reseta intervalo ao (re)ativar
    intervalRef.current = baseInterval;
    schedule();

    // Retoma polling quando aba volta a ficar visível
    const onVisibility = () => {
      if (!document.hidden && pauseHidden) {
        clearTimeout(timerRef.current);
        intervalRef.current = baseInterval; // reseta backoff ao retornar
        schedule();
      }
    };

    if (pauseHidden) {
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      clearTimeout(timerRef.current);
      if (pauseHidden) {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
  }, [enabled, schedule, baseInterval, pauseHidden]);
}
